import { apiClientRegistryAvailable } from '@/app/core/current-user/api-client-registry-scope';
import { CurrentUserService, PermissionSpec } from '@/app/core/current-user/current-user.service';

/**
 * The operator screens whose route carries a `requirePermission` guard, with the spec it checks. ONE table feeds the
 * route guards (`app.routes.ts`), the operator menu and the dashboard's registry links, so a link is offered exactly where
 * its guard lets the actor through — none ends on /zabranjen-pristup (T1-06/T1-07). A path absent from the table has no
 * permission guard of its own.
 */
export const OPERATOR_ROUTE_PERMISSIONS = {
    '/ekosistem': 'global.ecosystem:VIEW',
    '/tenanti': 'global.tenants:VIEW',
    '/grupe': 'global.tenantGroups:VIEW',
    '/korisnici': 'global.userAccounts:VIEW',
    // UX pregled 5 (D-UX5-1): the SSO applications' registry had no guard, so anyone else landed on an empty, refused screen.
    '/federacija': 'global.externalIdentityConsumers:VIEW',
    '/entra-federacija': 'global.externalIdentityConsumers:VIEW',
    '/provajderi-prijave': 'global.externalIdentityConsumers:VIEW'
} as const satisfies Record<string, PermissionSpec>;

/**
 * The operator screens decided by a predicate over several flags rather than one spec (UX pregled 4): /api-klijenti is the
 * registry across tenants, offered only where its whole read is (`apiClientRegistryAvailable`, D-UX4-1).
 */
export const OPERATOR_ROUTE_PREDICATES: Readonly<Record<string, (currentUser: Pick<CurrentUserService, 'hasAny'>) => boolean>> = {
    '/api-klijenti': apiClientRegistryAvailable
};

/** Whether the route at `path` lets the actor through its own permission guard (true for a path without one). */
export function operatorRouteOpens(currentUser: CurrentUserService, path: string): boolean {
    const predicate = OPERATOR_ROUTE_PREDICATES[path];
    if (predicate) return predicate(currentUser);
    const spec = (OPERATOR_ROUTE_PERMISSIONS as Readonly<Record<string, PermissionSpec | undefined>>)[path];
    return !spec || currentUser.hasPermission(spec);
}
